import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Upload,
  X,
  Save,
  Check,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { fileUrl } from "@/lib/format";
import { SplitEditor, LivePreviewPane } from "@/components/admin/split-editor";
import { PRIMARY_COLOR_DEFAULT } from "@/lib/theme";
import { MORTGAGE_CALCULATOR_ENABLED } from "@/lib/feature-flags";
import type {
  CtaLink,
  PartnerItem,
  SiteSettingsData,
  StaticPage,
} from "@shared/schema";
import { DEFAULT_SITE_SETTINGS } from "@shared/defaults";
import { MaintenanceFields } from "@/components/admin/maintenance-fields";
import { HOME_ANCHOR_SECTIONS } from "@/components/cta-button";
import { partners as builtinPartners } from "@/data/partners";

const FORM_ID = "site-settings-form";

// The built-in logo strip pre-seeded as editable rows: until the admin saves a
// partners list, these are what the public site shows — so this is what the
// editor should start from (rename / replace logo / delete all work on them).
const BUILTIN_PARTNER_ITEMS: PartnerItem[] = builtinPartners.map((p) => ({
  name: p.name,
  logoKey: null,
  builtinSrc: p.src,
}));

/** Which live-site page + section a field group maps to in the preview. */
type PreviewTarget = { path: string; section: string | null };

export function AdminSiteSettings() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "site-settings"],
    queryFn: () => api<SiteSettingsData>("/api/admin/site-settings"),
  });

  const pagesQuery = useQuery({
    queryKey: ["admin", "pages"],
    queryFn: () => api<StaticPage[]>("/api/admin/pages"),
  });

  const [draft, setDraft] = useState<SiteSettingsData>(DEFAULT_SITE_SETTINGS);
  // JSON snapshot of the last saved state — drives the dirty / draft logic.
  const [saved, setSaved] = useState<string>("");
  const [preview, setPreview] = useState<PreviewTarget>({
    path: "/",
    section: "hero",
  });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (q.data) {
      setDraft(q.data);
      setSaved(JSON.stringify(q.data));
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      api<SiteSettingsData>("/api/admin/site-settings", {
        method: "PATCH",
        body: draft,
      }),
    onSuccess: (data) => {
      setDraft(data);
      setSaved(JSON.stringify(data));
      qc.invalidateQueries({ queryKey: ["admin", "site-settings"] });
      qc.invalidateQueries({ queryKey: ["site-settings"] });
      // Reload the preview so the freshly-saved changes appear in the iframe.
      setReloadToken((t) => t + 1);
    },
  });

  // Draft logic: nothing is persisted (and the live site is untouched) until
  // the values differ from the last saved snapshot AND Save is pressed.
  const dirty = saved !== "" && JSON.stringify(draft) !== saved;

  const updBrand = <K extends keyof SiteSettingsData["brand"]>(
    key: K,
    value: SiteSettingsData["brand"][K],
  ) => setDraft((d) => ({ ...d, brand: { ...d.brand, [key]: value } }));

  const updHome = <K extends keyof SiteSettingsData["home"]>(
    key: K,
    value: SiteSettingsData["home"][K],
  ) => setDraft((d) => ({ ...d, home: { ...d.home, [key]: value } }));

  const updAbout = <K extends keyof SiteSettingsData["about"]>(
    key: K,
    value: SiteSettingsData["about"][K],
  ) => setDraft((d) => ({ ...d, about: { ...d.about, [key]: value } }));

  const updContact = <K extends keyof SiteSettingsData["contact"]>(
    key: K,
    value: SiteSettingsData["contact"][K],
  ) => setDraft((d) => ({ ...d, contact: { ...d.contact, [key]: value } }));

  // SEO — optional block. When left empty, the server auto-derives title +
  // description from the brand / hero copy (server/seo.ts), so a blank form
  // still ships correct, brand-safe meta.
  const updSeo = <K extends keyof NonNullable<SiteSettingsData["seo"]>>(
    key: K,
    value: NonNullable<SiteSettingsData["seo"]>[K],
  ) =>
    setDraft((d) => ({ ...d, seo: { ...(d.seo ?? {}), [key]: value } }));

  // „Hamarosan” mód — opcionális blokk: hiányzó érték = az oldal éles.
  const updMaintenance = (
    patch: Partial<NonNullable<SiteSettingsData["maintenance"]>>,
  ) =>
    setDraft((d) => ({
      ...d,
      maintenance: { ...(d.maintenance ?? {}), ...patch },
    }));

  // "Partnereink" — the block is optional (backward-compat), so it is only
  // materialised in the draft once the admin actually edits it. Until then the
  // editor shows the built-in logos as editable rows (they are what the public
  // site renders), and the first edit copies them into the draft.
  const updPartners = (
    patch: Partial<NonNullable<SiteSettingsData["partners"]>>,
  ) =>
    setDraft((d) => ({
      ...d,
      partners: {
        items: d.partners?.items ?? BUILTIN_PARTNER_ITEMS,
        ...(d.partners ?? {}),
        ...patch,
      },
    }));

  const partnerItems = draft.partners?.items ?? BUILTIN_PARTNER_ITEMS;

  const updatePartnerItem = (index: number, patch: Partial<PartnerItem>) => {
    const items = [...partnerItems];
    items[index] = { ...items[index], ...patch };
    updPartners({ items });
  };

  const movePartnerItem = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= partnerItems.length) return;
    const items = [...partnerItems];
    [items[index], items[target]] = [items[target], items[index]];
    updPartners({ items });
  };

  // "Hitel kalkulátor" — optional block (backward-compat): materialised in the
  // draft only once the admin touches it. Absent → the public calculator keeps
  // its default behaviour (visible, live MNB rate).
  const updMortgage = (
    patch: Partial<NonNullable<SiteSettingsData["mortgage"]>>,
  ) =>
    setDraft((d) => ({
      ...d,
      mortgage: { ...(d.mortgage ?? {}), ...patch },
    }));

  const updMortgagePartner = (
    patch: Partial<NonNullable<NonNullable<SiteSettingsData["mortgage"]>["partner"]>>,
  ) =>
    setDraft((d) => ({
      ...d,
      mortgage: {
        ...(d.mortgage ?? {}),
        partner: {
          name: d.mortgage?.partner?.name ?? "",
          logoKey: d.mortgage?.partner?.logoKey ?? null,
          ...(d.mortgage?.partner ?? {}),
          ...patch,
        },
      },
    }));

  const mortgage = draft.mortgage;
  const mortgagePartner = mortgage?.partner;

  // "Hitelügyintézés" doboz — a főoldalon a bannerek alatt és a lakás
  // oldalakon jelenik meg. Ugyanaz az opcionális-blokk logika: csak akkor
  // kerül a draftba, ha az admin hozzányúl.
  const updAdvisor = (
    patch: Partial<NonNullable<SiteSettingsData["mortgageAdvisor"]>>,
  ) =>
    setDraft((d) => ({
      ...d,
      mortgageAdvisor: { ...(d.mortgageAdvisor ?? {}), ...patch },
    }));

  // Üres mezőnél az alapértelmezett szöveg jelenik meg az élő oldalon, ezért
  // azt mutatjuk placeholderként.
  const advisor = draft.mortgageAdvisor;
  const advisorFallback = DEFAULT_SITE_SETTINGS.mortgageAdvisor ?? {};

  function activate(path: string, section: string | null) {
    setPreview((p) =>
      p.path === path && p.section === section ? p : { path, section },
    );
  }

  const toolbar = (
    <>
      {save.isError && (
        <span className="hidden sm:inline text-xs text-red-600 max-w-[200px] truncate">
          {save.error instanceof ApiError ? save.error.message : "Mentési hiba"}
        </span>
      )}
      {!dirty && save.isSuccess && (
        <span className="hidden sm:inline-flex items-center gap-1 text-xs text-emerald-700">
          <Check size={14} /> Mentve
        </span>
      )}
      {dirty && (
        <span className="hidden sm:inline text-xs text-amber-600">
          Nem mentett változások
        </span>
      )}
      <button
        type="submit"
        form={FORM_ID}
        disabled={!dirty || save.isPending}
        className="btn-primary !py-2.5 !px-5 text-xs"
      >
        <Save size={15} />
        {save.isPending ? "Mentés..." : "Mentés"}
      </button>
    </>
  );

  const previewPane = (
    <LivePreviewPane
      path={preview.path}
      section={preview.section}
      reloadToken={reloadToken}
    />
  );

  if (q.isLoading) {
    return (
      <div className="p-8 text-brand-500">Beállítások betöltése…</div>
    );
  }

  return (
    <SplitEditor
      title="Oldal beállítások"
      subtitle="Brand, főoldal, rólunk és kapcsolat — élő előnézettel"
      toolbar={toolbar}
      preview={previewPane}
    >
      <form
        id={FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty) save.mutate();
        }}
        className="space-y-8 pb-10"
      >
        {/* ─── Oldal állapota („Hamarosan” mód) ─────────────── */}
        <Section
          title="Oldal állapota"
          subtitle={
            "Éles az oldal, vagy „Hamarosan” felirat fogadja a látogatókat? " +
            "Bekapcsolva a teljes publikus tartalom (projektek, lakások, árak, " +
            "kapcsolat) eltűnik — de bejelentkezett adminként Ön továbbra is " +
            "látja és javíthatja. Semmi nem törlődik, egy kattintással visszakapcsolható."
          }
        >
          <MaintenanceFields
            value={draft.maintenance}
            onChange={updMaintenance}
            scope="site"
          />
        </Section>

        {/* ─── Brand ────────────────────────────────────────────────── */}
        <FocusZone onActivate={() => activate("/", "hero")}>
          <Section title="Brand" subtitle="Cég adatai, megjelenik mindenhol">
            <Grid2>
              <Field label="Cég név (teljes)">
                <input
                  className="input"
                  value={draft.brand.name}
                  onChange={(e) => updBrand("name", e.target.value)}
                />
              </Field>
              <Field label="Cég név (rövid, fejlécben)">
                <input
                  className="input"
                  value={draft.brand.shortName}
                  onChange={(e) => updBrand("shortName", e.target.value)}
                />
              </Field>
            </Grid2>
            <Field label="Tagline (footer alatt)">
              <input
                className="input"
                value={draft.brand.tagline}
                onChange={(e) => updBrand("tagline", e.target.value)}
              />
            </Field>
            <Grid2>
              <Field label="Email">
                <input
                  type="email"
                  className="input"
                  value={draft.brand.email}
                  onChange={(e) => updBrand("email", e.target.value)}
                />
              </Field>
              <Field label="Telefon">
                <input
                  type="tel"
                  className="input"
                  value={draft.brand.phone}
                  onChange={(e) => updBrand("phone", e.target.value)}
                />
              </Field>
            </Grid2>
            <Field label="Cím">
              <input
                className="input"
                value={draft.brand.address}
                onChange={(e) => updBrand("address", e.target.value)}
              />
            </Field>
            <Grid2>
              <Field label="Logo betű (kép helyett)">
                <input
                  maxLength={1}
                  className="input w-20 text-center font-heading text-xl uppercase"
                  value={draft.brand.logoLetter}
                  onChange={(e) => updBrand("logoLetter", e.target.value)}
                />
              </Field>
              <Field label="Logo / fejléc kép (felülírja a betűt)">
                <ImageUpload
                  currentKey={draft.brand.logoImageKey ?? null}
                  prefix="brand"
                  onChange={(key) => updBrand("logoImageKey", key)}
                />
              </Field>
            </Grid2>
            <Field label="Branding — elsődleges szín (primary color)">
              <ColorPicker
                value={draft.brand.primaryColor ?? PRIMARY_COLOR_DEFAULT}
                onChange={(hex) => updBrand("primaryColor", hex)}
              />
            </Field>
            <Grid2>
              <Field label="Facebook URL">
                <input
                  className="input"
                  placeholder="https://facebook.com/..."
                  value={draft.brand.socials.facebook ?? ""}
                  onChange={(e) =>
                    updBrand("socials", {
                      ...draft.brand.socials,
                      facebook: e.target.value || null,
                    })
                  }
                />
              </Field>
              <Field label="Instagram URL">
                <input
                  className="input"
                  placeholder="https://instagram.com/..."
                  value={draft.brand.socials.instagram ?? ""}
                  onChange={(e) =>
                    updBrand("socials", {
                      ...draft.brand.socials,
                      instagram: e.target.value || null,
                    })
                  }
                />
              </Field>
            </Grid2>
          </Section>
        </FocusZone>

        {/* ─── SEO / megosztás ──────────────────────────────────────── */}
        <FocusZone onActivate={() => activate("/", "hero")}>
          <Section
            title="SEO / megosztás"
            subtitle="Böngészőcím és keresőleírás. Üresen hagyva automatikusan a brand + hero szövegből generálódik — konkrét cégnevet nem kell beírni."
          >
            <div className="space-y-4">
              <Field label="Oldalcím (title) — opcionális felülírás">
                <input
                  className="input"
                  value={draft.seo?.metaTitle ?? ""}
                  placeholder={`${draft.brand.name} — ${draft.brand.tagline}`}
                  onChange={(e) => updSeo("metaTitle", e.target.value)}
                />
              </Field>
              <Field label="Meta leírás (keresőben / megosztáskor) — opcionális">
                <textarea
                  className="input min-h-[80px]"
                  rows={3}
                  value={draft.seo?.metaDescription ?? ""}
                  placeholder={
                    draft.home.heroSubtitle || draft.brand.tagline
                  }
                  onChange={(e) => updSeo("metaDescription", e.target.value)}
                />
              </Field>
              <p className="text-xs text-brand-400">
                A projekt-oldalak automatikusan a projekt nevét és rövid
                leírását használják. A megosztási előnézeti kép a feltöltött
                logó, ha nincs külön beállítva.
              </p>
            </div>
          </Section>
        </FocusZone>

        {/* ─── Home page ────────────────────────────────────────────── */}
        <Section title="Kezdőoldal" subtitle="Hero szekció + CTA strip szövegek">
          <FocusZone onActivate={() => activate("/", "hero")} className="space-y-5">
            <Field label="Hero eyebrow (kicsi piros szöveg)">
              <input
                className="input"
                value={draft.home.heroEyebrow}
                onChange={(e) => updHome("heroEyebrow", e.target.value)}
              />
            </Field>
            <Field label="Hero főcím">
              <input
                className="input"
                value={draft.home.heroTitle}
                onChange={(e) => updHome("heroTitle", e.target.value)}
              />
            </Field>
            <Field label="Hero alcím">
              <textarea
                rows={3}
                className="input"
                value={draft.home.heroSubtitle}
                onChange={(e) => updHome("heroSubtitle", e.target.value)}
              />
            </Field>
            <Field label="Hero háttérkép">
              <ImageUpload
                currentKey={draft.home.heroImageKey ?? null}
                prefix="home/hero"
                onChange={(key) => updHome("heroImageKey", key)}
              />
            </Field>
            <Grid2>
              <Field label="CTA gomb 1: Projektek">
                <input
                  className="input"
                  value={draft.home.ctaProjectsText}
                  onChange={(e) => updHome("ctaProjectsText", e.target.value)}
                />
              </Field>
              <Field label="CTA gomb 2: Kapcsolat">
                <input
                  className="input"
                  value={draft.home.ctaContactText}
                  onChange={(e) => updHome("ctaContactText", e.target.value)}
                />
              </Field>
            </Grid2>
            <CtaLinkEditor
              label="CTA gomb 1"
              value={draft.home.ctaProjectsLink}
              fallbackHref="/projektek"
              pages={pagesQuery.data ?? []}
              onChange={(link) => updHome("ctaProjectsLink", link)}
            />
            <CtaLinkEditor
              label="CTA gomb 2"
              value={draft.home.ctaContactLink}
              fallbackHref="/kapcsolat"
              pages={pagesQuery.data ?? []}
              onChange={(link) => updHome("ctaContactLink", link)}
            />
          </FocusZone>
          <FocusZone
            onActivate={() => activate("/", "services")}
            className="grid sm:grid-cols-2 gap-4"
          >
            <Field label="Szolgáltatások eyebrow">
              <input
                className="input"
                value={draft.home.servicesEyebrow}
                onChange={(e) => updHome("servicesEyebrow", e.target.value)}
              />
            </Field>
            <Field label="Szolgáltatások cím">
              <input
                className="input"
                value={draft.home.servicesTitle}
                onChange={(e) => updHome("servicesTitle", e.target.value)}
              />
            </Field>
          </FocusZone>
          <FocusZone
            onActivate={() => activate("/", "projects")}
            className="grid sm:grid-cols-2 gap-4"
          >
            <Field label="Projektek eyebrow">
              <input
                className="input"
                value={draft.home.projectsEyebrow}
                onChange={(e) => updHome("projectsEyebrow", e.target.value)}
              />
            </Field>
            <Field label="Projektek cím">
              <input
                className="input"
                value={draft.home.projectsTitle}
                onChange={(e) => updHome("projectsTitle", e.target.value)}
              />
            </Field>
          </FocusZone>
          <FocusZone
            onActivate={() => activate("/", "cta")}
            className="space-y-5"
          >
            <Field label="CTA strip cím (alsó dark sáv)">
              <input
                className="input"
                value={draft.home.ctaStripTitle}
                onChange={(e) => updHome("ctaStripTitle", e.target.value)}
              />
            </Field>
            <Field label="CTA strip alcím">
              <input
                className="input"
                value={draft.home.ctaStripSubtitle}
                onChange={(e) => updHome("ctaStripSubtitle", e.target.value)}
              />
            </Field>
            <Field label="CTA strip gomb szöveg">
              <input
                className="input"
                value={draft.home.ctaStripButtonText}
                onChange={(e) => updHome("ctaStripButtonText", e.target.value)}
              />
            </Field>
            <CtaLinkEditor
              label="CTA sáv gomb"
              value={draft.home.ctaStripLink}
              fallbackHref="/kapcsolat"
              pages={pagesQuery.data ?? []}
              onChange={(link) => updHome("ctaStripLink", link)}
            />
          </FocusZone>
        </Section>

        {/* ─── Partners (home page logo strip) ─────────────────────── */}
        <FocusZone onActivate={() => activate("/", "partners")}>
          <Section
            title="Partnereink"
            subtitle="A főoldal alján megjelenő partner logók. A beépített logók előre fel vannak véve — átnevezhetők, lecserélhetők vagy törölhetők."
          >
            <Field label="Szekció cím">
              <input
                className="input"
                placeholder="Partnereink"
                value={draft.partners?.title ?? ""}
                onChange={(e) => updPartners({ title: e.target.value })}
              />
            </Field>
            <div>
              <label className="label">Partnerek</label>
              <div className="space-y-3">
                {partnerItems.map((p, i) => (
                  <div
                    key={i}
                    className="flex flex-wrap items-start gap-3 rounded-lg border border-brand-100 bg-brand-50/40 p-3"
                  >
                    <div className="flex-1 min-w-[180px]">
                      <input
                        className="input"
                        placeholder="Partner neve"
                        value={p.name}
                        onChange={(e) =>
                          updatePartnerItem(i, { name: e.target.value })
                        }
                      />
                      <p className="mt-1 text-[11px] text-brand-500">
                        Logó nélkül a név szövegként jelenik meg.
                      </p>
                    </div>
                    <div className="shrink-0">
                      <ImageUpload
                        currentKey={p.logoKey}
                        fallbackSrc={p.builtinSrc}
                        prefix="partners"
                        onChange={(key) =>
                          updatePartnerItem(i, { logoKey: key })
                        }
                      />
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      <button
                        type="button"
                        title="Feljebb"
                        disabled={i === 0}
                        className="p-1.5 rounded text-brand-500 hover:text-brand-900 hover:bg-brand-100 disabled:opacity-30 disabled:pointer-events-none"
                        onClick={() => movePartnerItem(i, -1)}
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        type="button"
                        title="Lejjebb"
                        disabled={i === partnerItems.length - 1}
                        className="p-1.5 rounded text-brand-500 hover:text-brand-900 hover:bg-brand-100 disabled:opacity-30 disabled:pointer-events-none"
                        onClick={() => movePartnerItem(i, 1)}
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button
                        type="button"
                        title="Törlés"
                        className="p-1.5 rounded text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() =>
                          updPartners({
                            items: partnerItems.filter((_, j) => j !== i),
                          })
                        }
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-secondary text-xs mt-1"
                  onClick={() =>
                    updPartners({
                      items: [...partnerItems, { name: "", logoKey: null }],
                    })
                  }
                >
                  <Plus size={14} /> Új partner
                </button>
              </div>
            </div>
          </Section>
        </FocusZone>

        {/* ─── Hitelügyintézés doboz (főoldal + lakás oldalak) ─────── */}
        <FocusZone onActivate={() => activate("/", "mortgage-advisor")}>
          <Section
            title="Hitelügyintézés"
            subtitle="A főoldalon a lapozós bannerek alatt és minden lakás oldalán megjelenő felhívás. A CTA gombra nyílik a visszahívás-kérő űrlap; a beérkező kérések az Üzenetek menüben és a központi értesítési e-mail címen jelennek meg."
          >
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={advisor?.enabled !== false}
                onChange={(e) => updAdvisor({ enabled: e.target.checked })}
              />
              <span className="text-sm">
                Doboz megjelenítése a főoldalon és a lakás oldalakon
              </span>
            </label>

            <Field label="Felhívás címe">
              <input
                className="input"
                placeholder={advisorFallback.title}
                value={advisor?.title ?? ""}
                onChange={(e) => updAdvisor({ title: e.target.value })}
              />
            </Field>

            <Field label="Felhívás szövege">
              <textarea
                className="input min-h-[80px]"
                placeholder={advisorFallback.subtitle}
                value={advisor?.subtitle ?? ""}
                onChange={(e) => updAdvisor({ subtitle: e.target.value })}
              />
            </Field>

            <Grid2>
              <Field label="CTA gomb felirata">
                <input
                  className="input"
                  placeholder={advisorFallback.ctaText}
                  value={advisor?.ctaText ?? ""}
                  onChange={(e) => updAdvisor({ ctaText: e.target.value })}
                />
              </Field>
              <Field label="Űrlap bevezető szövege">
                <input
                  className="input"
                  placeholder={advisorFallback.formIntro}
                  value={advisor?.formIntro ?? ""}
                  onChange={(e) => updAdvisor({ formIntro: e.target.value })}
                />
              </Field>
            </Grid2>

            <div className="border-t border-brand-100 pt-5 space-y-4">
              <div>
                <h3 className="font-medium text-sm">Hitelügyintéző adatai</h3>
                <p className="text-xs text-brand-500 mt-1">
                  A név és a telefonszám megjelenik a dobozban (a telefonszám
                  kattintható). Az e-mail cím szándékosan NEM jelenik meg az
                  oldalon — csak itt, nyilvántartásként; az űrlap tartalma a
                  központi értesítési címre érkezik.
                </p>
              </div>
              <Grid2>
                <Field label="Név">
                  <input
                    className="input"
                    placeholder={advisorFallback.advisorName}
                    value={advisor?.advisorName ?? ""}
                    onChange={(e) =>
                      updAdvisor({ advisorName: e.target.value })
                    }
                  />
                </Field>
                <Field label="Telefonszám">
                  <input
                    className="input"
                    placeholder={advisorFallback.advisorPhone}
                    value={advisor?.advisorPhone ?? ""}
                    onChange={(e) =>
                      updAdvisor({ advisorPhone: e.target.value })
                    }
                  />
                </Field>
              </Grid2>
              <Field label="E-mail cím (nem jelenik meg az oldalon)">
                <input
                  className="input"
                  type="email"
                  placeholder="hitel@example.com"
                  value={advisor?.advisorEmail ?? ""}
                  onChange={(e) =>
                    updAdvisor({ advisorEmail: e.target.value })
                  }
                />
              </Field>
            </div>
          </Section>
        </FocusZone>

        {/* ─── Mortgage calculator ─────────────────────────────────────
            Elrejtve (MORTGAGE_CALCULATOR_ENABLED = false) — a megrendelő
            kérésére a hitel kalkulátor sem az élő oldalon, sem itt nem
            látszik. A beállítások a háttérben megmaradnak; visszakapcsolni a
            client/src/lib/feature-flags.ts kapcsolójával lehet. */}
        {MORTGAGE_CALCULATOR_ENABLED && (
        <Section
          title="Hitel kalkulátor"
          subtitle="A lakás oldalakon megjelenő kalkulátor. Alapból automatikus MNB jegybanki alapkamattal működik — itt kézzel felülírható a kamat, ki-be kapcsolható, és külsős hitel-partner is megjeleníthető mellette."
        >
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={mortgage?.enabled !== false}
              onChange={(e) => updMortgage({ enabled: e.target.checked })}
            />
            <span className="text-sm">
              Kalkulátor megjelenítése a lakás oldalakon
            </span>
          </label>

          <Field label="Kézi kamatláb felülírás (%)">
            <ManualRateField
              value={mortgage?.manualRatePct ?? null}
              onChange={(v) => updMortgage({ manualRatePct: v })}
            />
            <p className="mt-1 text-[11px] text-brand-500">
              Ha kitöltöd, a kalkulátor ezt használja az MNB-érték helyett, és a
              forrásfelirat „tájékoztató kamatláb”-ra vált. Üresen hagyva az
              automatikus MNB alapkamat marad.
            </p>
          </Field>

          <Field label="Jogi megjegyzés (disclaimer)">
            <textarea
              className="input min-h-[80px]"
              placeholder="Üresen hagyva az alapértelmezett tájékoztató szöveg jelenik meg."
              value={mortgage?.disclaimer ?? ""}
              onChange={(e) => updMortgage({ disclaimer: e.target.value })}
            />
          </Field>

          {/* Optional external mortgage-advisor partner card */}
          <div className="border-t border-brand-100 pt-5 space-y-4">
            <div>
              <h3 className="font-medium text-sm">Hitel-tanácsadó partner</h3>
              <p className="text-xs text-brand-500 mt-1">
                Opcionális kártya a kalkulátor alatt. Töltsd ki a nevet, hogy
                megjelenjen — a többi mező elhagyható.
              </p>
            </div>
            <Grid2>
              <Field label="Partner neve">
                <input
                  className="input"
                  placeholder="pl. Hitel Tanácsadó Kft."
                  value={mortgagePartner?.name ?? ""}
                  onChange={(e) => updMortgagePartner({ name: e.target.value })}
                />
              </Field>
              <Field label="Logó">
                <ImageUpload
                  currentKey={mortgagePartner?.logoKey ?? null}
                  prefix="mortgage"
                  onChange={(key) => updMortgagePartner({ logoKey: key })}
                />
              </Field>
            </Grid2>
            <Grid2>
              <Field label="Telefon">
                <input
                  className="input"
                  placeholder="+36 ..."
                  value={mortgagePartner?.phone ?? ""}
                  onChange={(e) => updMortgagePartner({ phone: e.target.value })}
                />
              </Field>
              <Field label="E-mail">
                <input
                  className="input"
                  type="email"
                  placeholder="partner@example.com"
                  value={mortgagePartner?.email ?? ""}
                  onChange={(e) => updMortgagePartner({ email: e.target.value })}
                />
              </Field>
            </Grid2>
            <Field label="Weboldal / link">
              <input
                className="input"
                type="url"
                placeholder="https://..."
                value={mortgagePartner?.url ?? ""}
                onChange={(e) => updMortgagePartner({ url: e.target.value })}
              />
            </Field>
          </div>
        </Section>
        )}

        {/* ─── About page ──────────────────────────────────────────── */}
        <FocusZone onActivate={() => activate("/rolunk", "about-hero")}>
          <Section title="Rólunk oldal">
            <Grid2>
              <Field label="Eyebrow">
                <input
                  className="input"
                  value={draft.about.pageEyebrow}
                  onChange={(e) => updAbout("pageEyebrow", e.target.value)}
                />
              </Field>
              <Field label="Oldal cím (\n = sortörés)">
                <input
                  className="input"
                  value={draft.about.pageTitle}
                  onChange={(e) => updAbout("pageTitle", e.target.value)}
                />
              </Field>
            </Grid2>
            <Field label="Bevezető">
              <textarea
                rows={4}
                className="input"
                value={draft.about.intro}
                onChange={(e) => updAbout("intro", e.target.value)}
              />
            </Field>
            <Grid2>
              <Field label="Vízió cím">
                <input
                  className="input"
                  value={draft.about.visionTitle}
                  onChange={(e) => updAbout("visionTitle", e.target.value)}
                />
              </Field>
              <Field label="Értékek cím">
                <input
                  className="input"
                  value={draft.about.valuesTitle}
                  onChange={(e) => updAbout("valuesTitle", e.target.value)}
                />
              </Field>
            </Grid2>
            <Field label="Vízió szöveg">
              <textarea
                rows={4}
                className="input"
                value={draft.about.visionText}
                onChange={(e) => updAbout("visionText", e.target.value)}
              />
            </Field>
            <Field label="Értékek (egy sor = egy érték)">
              <textarea
                rows={4}
                className="input"
                value={draft.about.values.join("\n")}
                onChange={(e) =>
                  updAbout(
                    "values",
                    e.target.value
                      .split("\n")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
              />
            </Field>
            <div>
              <label className="label">Statisztikák (max 4)</label>
              <div className="space-y-2">
                {draft.about.stats.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      className="input flex-1 max-w-[140px]"
                      placeholder="20+"
                      value={s.number}
                      onChange={(e) => {
                        const next = [...draft.about.stats];
                        next[i] = { ...next[i], number: e.target.value };
                        updAbout("stats", next);
                      }}
                    />
                    <input
                      className="input flex-1"
                      placeholder="Év tapasztalat"
                      value={s.label}
                      onChange={(e) => {
                        const next = [...draft.about.stats];
                        next[i] = { ...next[i], label: e.target.value };
                        updAbout("stats", next);
                      }}
                    />
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 px-2"
                      onClick={() =>
                        updAbout(
                          "stats",
                          draft.about.stats.filter((_, j) => j !== i),
                        )
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                {draft.about.stats.length < 4 && (
                  <button
                    type="button"
                    className="btn-secondary text-xs mt-2"
                    onClick={() =>
                      updAbout("stats", [
                        ...draft.about.stats,
                        { number: "", label: "" },
                      ])
                    }
                  >
                    <Plus size={14} /> Új statisztika
                  </button>
                )}
              </div>
            </div>
            <Grid2>
              <Field label="Megközelítés eyebrow">
                <input
                  className="input"
                  value={draft.about.approachEyebrow}
                  onChange={(e) => updAbout("approachEyebrow", e.target.value)}
                />
              </Field>
              <Field label="Megközelítés cím">
                <input
                  className="input"
                  value={draft.about.approachTitle}
                  onChange={(e) => updAbout("approachTitle", e.target.value)}
                />
              </Field>
            </Grid2>
            <Field label="Megközelítés szöveg">
              <textarea
                rows={4}
                className="input"
                value={draft.about.approachText}
                onChange={(e) => updAbout("approachText", e.target.value)}
              />
            </Field>
          </Section>
        </FocusZone>

        {/* ─── Contact page ────────────────────────────────────────── */}
        <FocusZone onActivate={() => activate("/kapcsolat", "contact-hero")}>
          <Section title="Kapcsolat oldal">
            <Grid2>
              <Field label="Eyebrow">
                <input
                  className="input"
                  value={draft.contact.pageEyebrow}
                  onChange={(e) => updContact("pageEyebrow", e.target.value)}
                />
              </Field>
              <Field label="Cím">
                <input
                  className="input"
                  value={draft.contact.pageTitle}
                  onChange={(e) => updContact("pageTitle", e.target.value)}
                />
              </Field>
            </Grid2>
            <Field label="Alcím">
              <input
                className="input"
                value={draft.contact.pageSubtitle}
                onChange={(e) => updContact("pageSubtitle", e.target.value)}
              />
            </Field>
            <Grid2>
              <Field label="Csapat szekció eyebrow">
                <input
                  className="input"
                  value={draft.contact.teamSectionEyebrow}
                  onChange={(e) =>
                    updContact("teamSectionEyebrow", e.target.value)
                  }
                />
              </Field>
              <Field label="Csapat szekció cím">
                <input
                  className="input"
                  value={draft.contact.teamSectionTitle}
                  onChange={(e) =>
                    updContact("teamSectionTitle", e.target.value)
                  }
                />
              </Field>
            </Grid2>
            <Grid2>
              <Field label="Form szekció eyebrow">
                <input
                  className="input"
                  value={draft.contact.formSectionEyebrow}
                  onChange={(e) =>
                    updContact("formSectionEyebrow", e.target.value)
                  }
                />
              </Field>
              <Field label="Form szekció cím">
                <input
                  className="input"
                  value={draft.contact.formSectionTitle}
                  onChange={(e) =>
                    updContact("formSectionTitle", e.target.value)
                  }
                />
              </Field>
            </Grid2>

            {/* Privacy policy link for the form's consent checkbox */}
            <div className="border-t border-brand-100 pt-5 space-y-4">
              <div>
                <h3 className="font-medium text-sm">
                  Adatvédelmi tájékoztató link
                </h3>
                <p className="text-xs text-brand-500 mt-1">
                  Az érdeklődési form jelölőnégyzetében az „adatvédelmi
                  tájékoztató" szöveg erre a címre mutató linkké alakul (új
                  lapon nyílik meg). Hagyja üresen, ha sima szöveg maradjon.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="privacyPolicyLinkType"
                    checked={draft.contact.privacyPolicyLinkType === "external"}
                    onChange={() => {
                      updContact("privacyPolicyLinkType", "external");
                      updContact("privacyPolicyUrl", "");
                    }}
                  />
                  Külső URL
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="privacyPolicyLinkType"
                    checked={draft.contact.privacyPolicyLinkType === "internal"}
                    onChange={() => {
                      updContact("privacyPolicyLinkType", "internal");
                      updContact("privacyPolicyUrl", "");
                    }}
                  />
                  Belső oldal
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="privacyPolicyLinkType"
                    checked={!draft.contact.privacyPolicyLinkType}
                    onChange={() => {
                      updContact("privacyPolicyLinkType", null);
                      updContact("privacyPolicyUrl", null);
                    }}
                  />
                  Nincs link
                </label>
              </div>

              {draft.contact.privacyPolicyLinkType === "external" && (
                <Field label="Külső URL">
                  <input
                    className="input"
                    type="url"
                    placeholder="https://..."
                    value={draft.contact.privacyPolicyUrl ?? ""}
                    onChange={(e) =>
                      updContact("privacyPolicyUrl", e.target.value)
                    }
                  />
                </Field>
              )}

              {draft.contact.privacyPolicyLinkType === "internal" && (
                <Field label="Belső oldal">
                  <select
                    className="input"
                    value={draft.contact.privacyPolicyUrl ?? ""}
                    onChange={(e) =>
                      updContact("privacyPolicyUrl", e.target.value || null)
                    }
                  >
                    <option value="">— válasszon oldalt —</option>
                    {(pagesQuery.data ?? []).map((p) => (
                      <option key={p.id} value={`/o/${p.slug}`}>
                        {p.title} (/o/{p.slug})
                      </option>
                    ))}
                  </select>
                  {pagesQuery.isLoading && (
                    <p className="mt-1 text-xs text-brand-500">
                      Oldalak betöltése...
                    </p>
                  )}
                </Field>
              )}
            </div>
          </Section>
        </FocusZone>
      </form>
    </SplitEditor>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Wraps a field group so that focusing (or clicking) anywhere inside it asks
 * the live preview to jump to the related page section.
 */
function FocusZone({
  onActivate,
  className,
  children,
}: {
  onActivate: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={className}
      onFocusCapture={onActivate}
      onMouseDownCapture={onActivate}
    >
      {children}
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="card p-6 lg:p-7 space-y-5">
      <div className="border-b border-brand-100 pb-4">
        <h2 className="font-semibold text-lg">{title}</h2>
        {subtitle && <p className="text-xs text-brand-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Grid2({ children }: { children: ReactNode }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

/** Fixed internal SPA routes offered in the CTA page dropdown. */
const CTA_INTERNAL_PAGES: { value: string; label: string }[] = [
  { value: "/", label: "Főoldal" },
  { value: "/projektek", label: "Projektek" },
  { value: "/kapcsolat", label: "Kapcsolat" },
];

/**
 * Editor for a single CTA button's navigation target. Lets the admin pick
 * between an internal page, an external URL, or an on-page section anchor.
 */
function CtaLinkEditor({
  label,
  value,
  fallbackHref,
  pages,
  onChange,
}: {
  label: string;
  value: CtaLink | undefined;
  fallbackHref: string;
  pages: StaticPage[];
  onChange: (link: CtaLink) => void;
}) {
  const linkType = value?.linkType ?? "page";
  const linkValue = value?.linkValue ?? "";
  const groupName = `cta-link-${label.replace(/\s+/g, "-")}`;

  const setType = (type: CtaLink["linkType"]) => {
    if (type === "page") onChange({ linkType: "page", linkValue: fallbackHref });
    else if (type === "url") onChange({ linkType: "url", linkValue: "" });
    else
      onChange({
        linkType: "anchor",
        linkValue: HOME_ANCHOR_SECTIONS[0].value,
      });
  };

  const publishedPages = pages.filter((p) => p.isPublished);

  return (
    <div className="rounded-lg border border-brand-100 bg-brand-50/40 p-4 space-y-3">
      <div className="text-xs font-semibold uppercase tracking-widish text-brand-600">
        {label} — hova mutasson?
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name={groupName}
            checked={linkType === "page"}
            onChange={() => setType("page")}
          />
          Oldal
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name={groupName}
            checked={linkType === "url"}
            onChange={() => setType("url")}
          />
          Webcím (URL)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name={groupName}
            checked={linkType === "anchor"}
            onChange={() => setType("anchor")}
          />
          Szekció az oldalon
        </label>
      </div>

      {linkType === "page" && (
        <Field label="Belső oldal">
          <select
            className="input"
            value={linkValue || fallbackHref}
            onChange={(e) =>
              onChange({ linkType: "page", linkValue: e.target.value })
            }
          >
            {CTA_INTERNAL_PAGES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label} ({p.value})
              </option>
            ))}
            {publishedPages.map((p) => (
              <option key={p.id} value={`/o/${p.slug}`}>
                {p.title} (/o/{p.slug})
              </option>
            ))}
          </select>
        </Field>
      )}

      {linkType === "url" && (
        <Field label="Külső URL (új lapon nyílik)">
          <input
            className="input"
            type="url"
            placeholder="https://..."
            value={linkValue}
            onChange={(e) =>
              onChange({ linkType: "url", linkValue: e.target.value })
            }
          />
        </Field>
      )}

      {linkType === "anchor" && (
        <Field label="Szekció a főoldalon">
          <select
            className="input"
            value={linkValue || HOME_ANCHOR_SECTIONS[0].value}
            onChange={(e) =>
              onChange({ linkType: "anchor", linkValue: e.target.value })
            }
          >
            {HOME_ANCHOR_SECTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      )}
    </div>
  );
}

/** Brand primary colour picker — swatch + native colour input + hex field. */
function ColorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (hex: string) => void;
}) {
  const normalized = /^#[0-9a-f]{6}$/i.test(value) ? value : PRIMARY_COLOR_DEFAULT;
  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={normalized}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-14 rounded-md border border-brand-200 bg-white p-1 cursor-pointer"
        aria-label="Szín választó"
      />
      <input
        className="input font-mono w-32 uppercase"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#2f7a3e"
      />
      <button
        type="button"
        onClick={() => onChange(PRIMARY_COLOR_DEFAULT)}
        className="text-xs text-brand-500 hover:text-brand-900 underline"
      >
        Alapértelmezett
      </button>
    </div>
  );
}

/**
 * Percentage input for the manual mortgage-rate override. Keeps its own text
 * state so a partial/decimal entry ("7," → "7,9") isn't clobbered by the
 * parsed number round-tripping back through a controlled value. Pushes the
 * parsed number up (or null when cleared / not yet valid).
 */
function ManualRateField({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const [text, setText] = useState(value != null ? String(value) : "");

  // Re-sync when the stored value changes from the outside (load / reset),
  // but not on every keystroke (the local text is the source of truth there).
  useEffect(() => {
    const parsed = text.trim().replace(",", ".");
    if (Number(parsed) !== value) {
      setText(value != null ? String(value) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      className="input max-w-[220px]"
      type="text"
      inputMode="decimal"
      placeholder="pl. 7,9 — üresen: MNB automatikus"
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        const raw = next.trim().replace(",", ".");
        if (raw === "") {
          onChange(null);
          return;
        }
        const n = Number(raw);
        // Ignore partial input (e.g. "7." → NaN via trailing dot is fine, but
        // "7,9" mid-type parses cleanly); only push finite positives.
        if (Number.isFinite(n) && n > 0) onChange(n);
      }}
    />
  );
}

function ImageUpload({
  currentKey,
  fallbackSrc,
  prefix,
  onChange,
}: {
  currentKey: string | null;
  /** Built-in (public asset) image shown while no upload replaces it. */
  fallbackSrc?: string;
  prefix: string;
  onChange: (key: string | null) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", prefix);
      const res = await api<{ key: string }>("/api/admin/upload", {
        method: "POST",
        formData: fd,
      });
      onChange(res.key);
    } finally {
      setUploading(false);
    }
  }

  return (
    // Vertical stack: preview (with its caption) on top, the upload/replace
    // button always UNDER it — inline flow let the button drift next to the
    // logo (F-MANUAL-BLZFZ324 tester report).
    <div className="flex flex-col items-start gap-2">
      {currentKey ? (
        <div className="relative inline-block">
          <img
            src={fileUrl(currentKey)}
            alt=""
            className="max-h-24 rounded-md border border-brand-200"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white grid place-items-center hover:bg-red-600"
          >
            <X size={14} />
          </button>
        </div>
      ) : fallbackSrc ? (
        <div className="inline-block">
          <img
            src={fallbackSrc}
            alt=""
            className="max-h-24 rounded-md border border-brand-200 bg-white p-1"
          />
          <p className="mt-0.5 text-[10px] text-brand-400">Beépített logó</p>
        </div>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload(f);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="btn-secondary text-xs"
      >
        <Upload size={14} />
        {uploading
          ? "Feltöltés..."
          : currentKey || fallbackSrc
            ? "Csere"
            : "Feltöltés"}
      </button>
    </div>
  );
}



